/**
 * Mystery Shopping — Insights & Consolidated Branch satisACTION® presentation layer.
 *
 * Scope: MYSTERY SHOPPING ONLY. Gated by tenantFeatures.has_mystery_shopping
 * at every call site (Dashboard.js, MysteryShoppingDashboard.jsx).
 *
 * This component is a PRESENTATION-ONLY refresh of the two existing MS
 * client views. It:
 *   • hits the SAME endpoints (`/api/reports/insights`, `/api/reports`)
 *   • reuses the SAME public_id → /reports/{public_id} deep-link route
 *   • does NOT alter analytics, scoring, tagging, satisACTION generation,
 *     branch aggregation or permissions
 *
 * All evidence-on-demand is delivered through a shared right-side Sheet
 * drawer so the surface stays clean: insight → click chip → FINDs → full
 * FIND, without losing context.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import {
  Activity, Download, TrendingUp, TrendingDown, MapPin, ArrowUpRight, Sparkle,
} from 'lucide-react';
import { toast } from 'sonner';

// Palette — softened VOX tones, no aggressive accents.
const TONE = {
  strength: {
    label:  'Strength',
    accent: '#1E9E68',            // deeper green for reliable contrast
    chipBg: 'bg-emerald-50',
    chipBd: 'border-emerald-200',
    chipTx: 'text-emerald-800',
    chipHover: 'hover:bg-emerald-100',
    ring:   'ring-emerald-300',
    dot:    'bg-emerald-500',
  },
  opportunity: {
    label:  'Opportunity',
    accent: '#B45309',
    chipBg: 'bg-amber-50',
    chipBd: 'border-amber-200',
    chipTx: 'text-amber-800',
    chipHover: 'hover:bg-amber-100',
    ring:   'ring-amber-300',
    dot:    'bg-amber-500',
  },
  attention: {
    label:  'Attention',
    accent: '#B91C1C',
    chipBg: 'bg-rose-50',
    chipBd: 'border-rose-200',
    chipTx: 'text-rose-800',
    chipHover: 'hover:bg-rose-100',
    ring:   'ring-rose-300',
    dot:    'bg-rose-500',
  },
  excellence: {
    label:  'Excellence',
    accent: '#1E9E68',
    chipBg: 'bg-emerald-50',
    chipBd: 'border-emerald-200',
    chipTx: 'text-emerald-800',
    chipHover: 'hover:bg-emerald-100',
    ring:   'ring-emerald-300',
    dot:    'bg-emerald-500',
  },
};

function fmtDate(iso) {
  if (!iso) return '';
  const s = String(iso).slice(0, 10);
  try {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
  } catch { return s; }
}

function formatBranchPeriod(start, end) {
  if (!start && !end) return '';
  const fmt = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.valueOf())) return '';
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short' });
  };
  const a = fmt(start), b = fmt(end);
  if (a && b && a === b) return a;
  if (a && b) return `${a} – ${b}`;
  return a || b || '';
}

/* ─── Drawer ───────────────────────────────────────────────────────────
   Shared right-side detail sheet. Opens with:
     • title    – insight phrase / branch name label
     • tone     – strength | opportunity | attention | excellence
     • context  – "Recurring strength", "Opportunity at Test Branch A", …
     • finds    – [{ public_id, branch_name, visit_date }]
   Every FIND row deep-links to `/reports/{public_id}` in a new tab so
   Management Insights context is preserved.
*/
function EvidenceSheet({ open, onOpenChange, payload }) {
  const tone = payload ? TONE[payload.tone] : TONE.strength;
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md md:max-w-lg overflow-y-auto bg-white"
        data-testid="ms-insight-drawer"
      >
        <SheetHeader className="text-left">
          <div className="flex items-center gap-2 mb-1">
            <span
              className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold border ${tone.chipBg} ${tone.chipTx} ${tone.chipBd}`}
              data-testid="ms-insight-drawer-tone"
            >
              <span className={`w-1.5 h-1.5 rounded-full ${tone.dot}`} />
              {payload?.toneLabel || tone.label}
            </span>
            {payload?.contextChip ? (
              <span className="text-[11px] text-[#64748B]">{payload.contextChip}</span>
            ) : null}
          </div>
          <SheetTitle className="text-lg font-semibold text-[#0F172A] font-['Montserrat']"
                      data-testid="ms-insight-drawer-title">
            {payload?.title || ''}
          </SheetTitle>
          {payload?.subtitle ? (
            <SheetDescription className="text-[13px] text-[#64748B]">
              {payload.subtitle}
            </SheetDescription>
          ) : null}
        </SheetHeader>

        <div className="mt-6">
          <p className="text-[11px] uppercase tracking-wider font-bold text-[#64748B] mb-2">
            {payload?.finds?.length || 0} supporting FIND{(payload?.finds?.length || 0) === 1 ? '' : 's'}
          </p>
          {(!payload?.finds || payload.finds.length === 0) ? (
            <p className="text-sm text-[#94A3B8] italic py-4">
              No FIND identifiers are attached to this insight yet.
            </p>
          ) : (
            <ul className="divide-y divide-[#F1F5F9] border border-[#E2E8F0] rounded-lg overflow-hidden">
              {payload.finds.map((f) => (
                <li
                  key={f.public_id}
                  className="px-3 py-3 hover:bg-[#F8FAFC] transition-colors"
                  data-testid={`ms-insight-drawer-find-${f.public_id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-[#0F172A] truncate">
                        {f.branch_name || '—'}
                      </p>
                      <p className="text-[11px] text-[#64748B] mt-0.5">
                        {f.visit_date ? fmtDate(f.visit_date) + ' · ' : ''}
                        <span className="font-mono text-[#1A3E6F]">{f.public_id}</span>
                      </p>
                    </div>
                    <a
                      href={`/reports/${f.public_id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="shrink-0 inline-flex items-center gap-1 text-[12px] font-semibold text-[#1A3E6F] hover:underline"
                      data-testid={`ms-insight-drawer-open-${f.public_id}`}
                    >
                      Open full FIND
                      <ArrowUpRight className="w-3.5 h-3.5" />
                    </a>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

/* ─── Reusable chip ───────────────────────────────────────────────── */
function InsightChip({ tone, label, count, countLabel, onClick, testid }) {
  const t = TONE[tone];
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex max-w-full items-center gap-2 px-3 py-1.5 rounded-full border ${t.chipBg} ${t.chipBd} ${t.chipTx} ${t.chipHover} transition-colors text-[12px] font-medium focus:outline-none focus:ring-2 ${t.ring}`}
      data-testid={testid}
    >
      <span className={`w-1.5 h-1.5 shrink-0 rounded-full ${t.dot}`} />
      <span className="min-w-0 max-w-[240px] truncate">{label}</span>
      {(count != null || countLabel) && (
        <span className="shrink-0 text-[11px] font-semibold opacity-70">
          {countLabel != null ? countLabel : `${count} FIND${count === 1 ? '' : 's'}`}
        </span>
      )}
    </button>
  );
}

/* ─── Section container ──────────────────────────────────────────── */
function InsightGroup({ title, tone, count, children, testid }) {
  const t = TONE[tone];
  return (
    <section
      className="rounded-xl border border-[#E2E8F0] bg-white p-5"
      data-testid={testid}
    >
      <div className="flex items-baseline justify-between gap-3 mb-4">
        <div className="flex items-center gap-2.5">
          <span className={`inline-block w-2 h-2 rounded-full ${t.dot}`} />
          <h4 className="text-[13px] font-semibold text-[#0F172A] font-['Montserrat']">
            {title}
          </h4>
        </div>
        <span className="text-[11px] text-[#64748B]">
          {count} {count === 1 ? 'insight' : 'insights'}
        </span>
      </div>
      {children}
    </section>
  );
}

function EmptyGroup({ label }) {
  return (
    <p className="text-[13px] text-[#94A3B8] italic py-3">
      Not enough published FINDs yet to surface {label}.
    </p>
  );
}

/* ─── Data hooks ─────────────────────────────────────────────────── */
function buildQuery({ dateParams, branchIds }) {
  const q = new URLSearchParams();
  if (dateParams?.from) q.append('date_from', dateParams.from);
  if (dateParams?.to) q.append('date_to', dateParams.to);
  if (branchIds) q.append('branch_ids', branchIds);
  return q.toString();
}

function useInsightsData({ dateParams, branchIds }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const qs = buildQuery({ dateParams, branchIds });
        const r = await fetch(`${API}/reports/insights?${qs}`, { credentials: 'include' });
        if (r.ok && alive) setData(await r.json());
        else if (alive) setData(null);
      } catch { if (alive) setData(null); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateParams?.from, dateParams?.to, branchIds]);
  return { data, loading };
}

/* Public-id → { branch_name, visit_date } lookup so the drawer can
   render evidence rows even though the /reports/insights payload
   only carries the FIND identifiers. */
function useFindLookup({ dateParams, branchIds }) {
  const [lookup, setLookup] = useState({});
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const qs = buildQuery({ dateParams, branchIds });
        const r = await fetch(`${API}/reports?${qs}`, { credentials: 'include' });
        if (!r.ok) return;
        const payload = await r.json();
        if (!alive) return;
        const map = {};
        for (const rep of (payload.reports || [])) {
          if (rep.public_id) {
            map[rep.public_id] = {
              branch_name: rep.branch_name || '—',
              visit_date:  rep.visit_datetime || rep.published_at || null,
            };
          }
        }
        setLookup(map);
      } catch { /* non-fatal — drawer just falls back to public_id */ }
    })();
    return () => { alive = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateParams?.from, dateParams?.to, branchIds]);
  return lookup;
}

/* ─── MANAGEMENT INSIGHTS CARD ───────────────────────────────────── */
function ManagementInsightsCardBody({ data, loading, onOpenChip, onOpenBranch, onDownload }) {
  return (
    <Card
      className="border-[#E2E8F0] shadow-sm"
      data-testid="ms-management-insights-card"
    >
      <CardHeader className="pb-4">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <CardTitle className="text-lg font-['Montserrat'] flex items-center gap-2 text-[#0F172A]">
              <Activity className="w-4.5 h-4.5 text-[#1A3E6F]" />
              Management Insights
            </CardTitle>
            <p className="text-[13px] text-[#64748B] mt-1.5 leading-relaxed max-w-2xl">
              Recurring themes from your published FINDs. Click any insight to see the FINDs behind it.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={onDownload}
            data-testid="ms-management-insights-summary-pdf"
            className="shrink-0"
          >
            <Download className="w-4 h-4 mr-1.5" />
            Management Summary PDF
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {loading ? (
          <p className="text-xs text-[#94A3B8] py-8 text-center">Loading…</p>
        ) : !data || data.totals?.published_finds === 0 ? (
          <p className="text-[13px] text-[#94A3B8] italic py-8 text-center">
            Not enough published FINDs yet to surface insights.
          </p>
        ) : (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <InsightGroup
                title="Recurring Strengths"
                tone="strength"
                count={data.recurring_strengths?.length || 0}
                testid="ms-insights-strengths"
              >
                {(!data.recurring_strengths || data.recurring_strengths.length === 0)
                  ? <EmptyGroup label="recurring strengths" />
                  : (
                    <div className="flex flex-wrap gap-2">
                      {data.recurring_strengths.map((it, i) => (
                        <InsightChip
                          key={i}
                          tone="strength"
                          label={it.phrase}
                          count={it.count}
                          onClick={() => onOpenChip({
                            tone: 'strength',
                            title: it.phrase,
                            subtitle: (it.branches || []).slice(0, 4).join(' · ') +
                                      ((it.branches?.length || 0) > 4 ? ` +${it.branches.length - 4} more` : ''),
                            contextChip: 'Management Insights',
                            public_ids: it.response_ids || [],
                          })}
                          testid={`ms-insight-strength-${i}`}
                        />
                      ))}
                    </div>
                  )}
              </InsightGroup>

              <InsightGroup
                title="Recurring Opportunities"
                tone="opportunity"
                count={data.recurring_opportunities?.length || 0}
                testid="ms-insights-opportunities"
              >
                {(!data.recurring_opportunities || data.recurring_opportunities.length === 0)
                  ? <EmptyGroup label="recurring opportunities" />
                  : (
                    <div className="flex flex-wrap gap-2">
                      {data.recurring_opportunities.map((it, i) => (
                        <InsightChip
                          key={i}
                          tone="opportunity"
                          label={it.phrase}
                          count={it.count}
                          onClick={() => onOpenChip({
                            tone: 'opportunity',
                            title: it.phrase,
                            subtitle: (it.branches || []).slice(0, 4).join(' · ') +
                                      ((it.branches?.length || 0) > 4 ? ` +${it.branches.length - 4} more` : ''),
                            contextChip: 'Management Insights',
                            public_ids: it.response_ids || [],
                          })}
                          testid={`ms-insight-opportunity-${i}`}
                        />
                      ))}
                    </div>
                  )}
              </InsightGroup>

              <InsightGroup
                title="Branches Needing Attention"
                tone="attention"
                count={data.branches_needing_attention?.length || 0}
                testid="ms-insights-attention"
              >
                {(!data.branches_needing_attention || data.branches_needing_attention.length === 0)
                  ? <EmptyGroup label="branches needing attention" />
                  : (
                    <div className="flex flex-wrap gap-2">
                      {data.branches_needing_attention.map((r) => (
                        <InsightChip
                          key={r.branch_id}
                          tone="attention"
                          label={r.branch_name}
                          countLabel={`TrustX® ${r.trustx_mean} · ${r.n_finds} FIND${r.n_finds === 1 ? '' : 's'}`}
                          onClick={() => onOpenBranch({
                            tone: 'attention',
                            title: r.branch_name,
                            subtitle: `TrustX® ${r.trustx_mean} · ${r.n_finds} published FIND${r.n_finds === 1 ? '' : 's'}`,
                            contextChip: 'Branches Needing Attention',
                            public_ids: r.response_ids || [],
                          })}
                          testid={`ms-insight-attention-${r.branch_id}`}
                        />
                      ))}
                    </div>
                  )}
              </InsightGroup>

              <InsightGroup
                title="Consistently Strong Branches"
                tone="excellence"
                count={data.consistently_strong_branches?.length || 0}
                testid="ms-insights-excellence"
              >
                {(!data.consistently_strong_branches || data.consistently_strong_branches.length === 0)
                  ? <EmptyGroup label="consistently strong branches" />
                  : (
                    <div className="flex flex-wrap gap-2">
                      {data.consistently_strong_branches.map((r) => (
                        <InsightChip
                          key={r.branch_id}
                          tone="excellence"
                          label={r.branch_name}
                          countLabel={`TrustX® ${r.trustx_mean} · ${r.n_finds} FIND${r.n_finds === 1 ? '' : 's'}`}
                          onClick={() => onOpenBranch({
                            tone: 'excellence',
                            title: r.branch_name,
                            subtitle: `TrustX® ${r.trustx_mean} · ${r.n_finds} published FIND${r.n_finds === 1 ? '' : 's'}`,
                            contextChip: 'Consistently Strong Branches',
                            public_ids: r.response_ids || [],
                          })}
                          testid={`ms-insight-excellence-${r.branch_id}`}
                        />
                      ))}
                    </div>
                  )}
              </InsightGroup>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

/* ─── CONSOLIDATED BRANCH satisACTION® CARD ──────────────────────── */
function BranchSatisactionRow({ branch, onOpenPhrase }) {
  const n = branch.n_finds || 0;
  const period = formatBranchPeriod(branch.period_start, branch.period_end);

  // For single-FIND branches we merge into flat lists (no "Recurring"
  // language) so the layout mirrors the current behaviour.
  const single = n === 1;
  const strengthsRecurring = single ? [] : (branch.recurring_strengths || []);
  const strengthsOther     = single
    ? [...(branch.recurring_strengths || []), ...(branch.other_strengths || [])]
    : (branch.other_strengths || []);
  const oppsRecurring = single ? [] : (branch.recurring_opportunities || []);
  const oppsOther     = single
    ? [...(branch.recurring_opportunities || []), ...(branch.other_opportunities || [])]
    : (branch.other_opportunities || []);

  const empty = strengthsRecurring.length + strengthsOther.length
              + oppsRecurring.length + oppsOther.length === 0;

  const renderList = (items, tone, section, groupLabel) => {
    if (!items || items.length === 0) return null;
    return (
      <div className="mt-3" data-testid={`ms-branch-${branch.branch_id}-${section}`}>
        <p className={`text-[11px] font-semibold uppercase tracking-wider mb-2 ${tone === 'strength' ? 'text-emerald-700' : 'text-amber-700'}`}>
          {groupLabel}
        </p>
        <div className="flex flex-wrap gap-2">
          {items.map((it, i) => (
            <InsightChip
              key={i}
              tone={tone}
              label={it.phrase}
              countLabel={single
                ? null
                : `${it.count} of ${n} FIND${n === 1 ? '' : 's'}`}
              onClick={() => onOpenPhrase({
                tone,
                title: it.phrase,
                subtitle: `${branch.branch_name}${period ? ' · ' + period : ''}`,
                contextChip: groupLabel,
                public_ids: it.public_ids || [],
              })}
              testid={`ms-branch-${branch.branch_id}-${section}-${i}`}
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <div
      className="rounded-xl border border-[#E2E8F0] bg-white p-5 hover:border-[#CBD5E1] transition-colors"
      data-testid={`ms-branch-sat-${branch.branch_id}`}
    >
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-1">
        <div className="flex items-center gap-2">
          <MapPin className="w-4 h-4 text-[#1A3E6F]" />
          <p className="text-[15px] font-semibold text-[#0F172A] font-['Montserrat']">
            {branch.branch_name}
          </p>
        </div>
        <p className="text-[11px] text-[#64748B]"
           data-testid={`ms-branch-sat-${branch.branch_id}-meta`}>
          {single ? 'Based on 1 published FIND' : `${n} FINDs analysed`}
          {period ? ` · ${period}` : ''}
        </p>
      </div>

      {empty ? (
        <p className="text-[13px] text-[#94A3B8] italic mt-3">
          No satisACTION® strengths or opportunities recorded on
          {single ? ' this FIND.' : ' these FINDs.'}
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
          <div>
            {renderList(strengthsRecurring, 'strength',   'rec-str',  'Recurring Strengths')}
            {renderList(strengthsOther,     'strength',   'oth-str',  single ? 'Strengths Identified' : 'Other Strengths')}
          </div>
          <div>
            {renderList(oppsRecurring, 'opportunity', 'rec-opp',  'Recurring Opportunities')}
            {renderList(oppsOther,     'opportunity', 'oth-opp',  single ? 'Opportunities Identified' : 'Other Opportunities')}
          </div>
        </div>
      )}
    </div>
  );
}

function ConsolidatedBranchSatisactionCard({ data, loading, onOpenPhrase }) {
  const branches = data?.consolidated_branch_satisaction || [];
  if (loading || branches.length === 0) return null;
  return (
    <Card
      className="border-[#E2E8F0] shadow-sm"
      data-testid="ms-consolidated-branch-satisaction-card"
    >
      <CardHeader className="pb-4">
        <CardTitle className="text-lg font-['Montserrat'] flex items-center gap-2 text-[#0F172A]">
          <Sparkle className="w-4.5 h-4.5 text-[#1A3E6F]" />
          Consolidated Branch satisACTION<span className="text-[10px] align-super">®</span>
        </CardTitle>
        <p className="text-[13px] text-[#64748B] mt-1.5 leading-relaxed max-w-2xl">
          A cumulative view for each branch across the current selection. Click any tag to see the FINDs behind it.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {branches.map((b) => (
          <BranchSatisactionRow key={b.branch_id} branch={b} onOpenPhrase={onOpenPhrase} />
        ))}
      </CardContent>
    </Card>
  );
}

/* ─── PUBLIC EXPORT: full Mystery Shopping insights section ─────── */
export default function MSInsightsSection({ dateParams, branchIds }) {
  const { data, loading } = useInsightsData({ dateParams, branchIds });
  const findLookup       = useFindLookup({ dateParams, branchIds });

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerPayload, setDrawerPayload] = useState(null);

  // Hydrate the drawer's supporting FIND rows from the shared lookup.
  const openDrawer = (raw) => {
    if (!raw) return;
    const ids = raw.public_ids || [];
    const finds = ids.map((pid) => ({
      public_id: pid,
      branch_name: findLookup[pid]?.branch_name || '—',
      visit_date:  findLookup[pid]?.visit_date  || null,
    }));
    setDrawerPayload({
      tone: raw.tone,
      toneLabel: TONE[raw.tone]?.label,
      title: raw.title,
      subtitle: raw.subtitle,
      contextChip: raw.contextChip,
      finds,
    });
    setDrawerOpen(true);
  };

  const downloadSummary = async () => {
    try {
      const qs = buildQuery({ dateParams, branchIds });
      const r = await fetch(`${API}/reports/pdf/management-summary?${qs}`, { credentials: 'include' });
      if (!r.ok) throw new Error('summary failed');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'satisFIND_Management_Summary.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Could not generate the Management Summary PDF');
    }
  };

  return (
    <div className="space-y-4" data-testid="ms-insights-section">
      <ManagementInsightsCardBody
        data={data}
        loading={loading}
        onOpenChip={openDrawer}
        onOpenBranch={openDrawer}
        onDownload={downloadSummary}
      />
      <ConsolidatedBranchSatisactionCard
        data={data}
        loading={loading}
        onOpenPhrase={openDrawer}
      />
      <EvidenceSheet
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        payload={drawerPayload}
      />
    </div>
  );
}
